import { TeamResource } from 'dy-frontend-http-repository/lib/modules/Team/resources';
import { useStore } from 'effector-react';
import React, { useEffect } from 'react';

import { useCustomSelectFormField, useForm } from '@app/hooks';
import { getStringRequiredValidator } from '@app/hooks/validation/functions';
import { Button, Card, Divider, FormGroup, Icon, Intent, MenuItem, Spinner } from '@blueprintjs/core';
import { ItemRendererProps } from '@blueprintjs/select';
import Flex from '@components/Flex';
import Heading from '@components/Heading';
import Overlay from '@components/Overlay';
import Select from '@components/Select';
import { ModalProps } from '@modals/types';

import { teamConnectionsApi } from '../../store/apis';

import { connectTeamToClient, fetchTeams } from './store/effects';
import { $teams } from './store/states';

export interface CreateClientTeamConnectionModalProps {
    userId: ID;
}

type Props = ModalProps<CreateClientTeamConnectionModalProps>;

const selectedTeamValidators = [getStringRequiredValidator()];

const CreateClientTeamConnectionModal: React.FC<Props> = ({ closeModal, data }) => {
    const teams = useStore($teams);

    const selectedTeam = useCustomSelectFormField<TeamResource | null>({
        id: 'selected-team',
        validators: selectedTeamValidators,
        initialValue: null,
        formatValue: (brandProfile) => (brandProfile ? brandProfile.title : ''),
    });

    const form = useForm({
        fields: [selectedTeam],
        apiCall: async () => {
            if (!selectedTeam.value || !data?.userId) {
                throw new Error('Team or User was not provided');
            }
            try {
                // Connect team to client
                const connectionRef = await connectTeamToClient({ userId: data.userId, teamId: selectedTeam.value.id });

                // Add team connection to the list of connection in local store
                teamConnectionsApi.create({
                    connectionId: connectionRef.id,
                    team: selectedTeam.value,
                    userId: data.userId,
                });

                return { response: true };
            } catch (e) {
                throw e;
            }
        },
        onSuccess: () => {
            closeModal?.();
        },
        onFailure: (error) => {
            // TODO: handle error
            console.error(error);
        },
    });

    useEffect(() => {
        if (teams !== null) {
            return;
        }

        fetchTeams({ is_archived: '0' }).catch((e) => {
            // TODO: handle error
            console.error(e);
        });
    }, []);

    if (!data) {
        closeModal?.();
        return null;
    }

    const renderTeamSelect = () => {
        if (!teams) {
            return null;
        }

        const renderItem = (item: TeamResource, { handleClick }: ItemRendererProps) => {
            const isMenuItemActive = !!selectedTeam.value && item.id === selectedTeam.value.id;

            return (
                <MenuItem
                    active={isMenuItemActive}
                    key={item.id}
                    text={item.title}
                    icon={<Icon size={16} icon="team" color={item.color} />}
                    onClick={handleClick}
                />
            );
        };

        const handleItemSelect = (item: TeamResource) => {
            selectedTeam.handleChange(item);
        };

        return (
            <FormGroup
                label="Team"
                intent={!!selectedTeam.error ? Intent.DANGER : Intent.NONE}
                helperText={selectedTeam.error}
            >
                <Select<TeamResource>
                    filterable
                    disabled={!teams || teams.items.length === 0}
                    items={teams ? teams.items : []}
                    itemRenderer={renderItem}
                    onItemSelect={handleItemSelect}
                    popoverProps={{ matchTargetWidth: true, usePortal: false }}
                    selectButtonProps={{
                        fill: true,
                        rightIcon: 'double-caret-vertical',
                        placeholder: 'Select team',
                        text: selectedTeam.value?.title,
                    }}
                />
            </FormGroup>
        );
    };

    const renderForm = () => {
        if (!teams) {
            return (
                <Flex direction="row" align="center" justify="center">
                    <Spinner />
                </Flex>
            );
        }

        return (
            <form onSubmit={form.handleFormSubmit}>
                {renderTeamSelect()}

                <Flex justify="flex-end">
                    <Button className="mr-1" outlined onClick={closeModal}>
                        Cancel
                    </Button>

                    <Button
                        disabled={form.hasFieldErrors}
                        loading={form.isSubmitting}
                        type="submit"
                        intent={Intent.PRIMARY}
                    >
                        Attach
                    </Button>
                </Flex>
            </form>
        );
    };

    return (
        <Overlay isOpen onClose={closeModal}>
            <Card style={{ width: '558px' }}>
                <Flex className="mb-2" align="center" justify="space-between">
                    <Heading type="h4">Attach team to client</Heading>
                    <Button minimal icon="cross" onClick={closeModal} />
                </Flex>

                <Divider className="mb-2" />

                {renderForm()}
            </Card>
        </Overlay>
    );
};

export default CreateClientTeamConnectionModal;
